import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Camera, Video, Minus, ChevronLeft, RotateCcw } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { resolveSharedStepRef } from '@jeraisonne/db';
import type { ChallengeMediaType, ShareType } from '@jeraisonne/db';
import { startBackgroundPublish } from '@/lib/challenges/background-publish';
import { ChallengeBackgroundPicker } from '@/features/challenge-reel/challenge-background-picker';
import { challengeMediaIssue } from '@/lib/challenges/challenge-media-format';
import {
  photoFrame,
  SELFIE_VIDEO_CONSTRAINTS,
  selfieRecorderOptions,
} from '@/lib/challenges/selfie-capture';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExerciseStep {
  id: string;
  prompt_md: string;
}

interface StepHistoryEntry {
  step: ExerciseStep;
  result: { wasCorrect: boolean };
  selectedChoiceId: string;
}

interface ChallengeShareModalProps {
  exerciseId: string;
  exerciseTitle: string;
  subject: string;
  level: string;
  chapter: string;
  phase: 'intro' | 'exercise';
  attemptId: string | null;
  currentStepIdx: number;
  currentStep: ExerciseStep | null;
  stepHistory: StepHistoryEntry[];
  userId: string;
  onClose: () => void;
}

type ModalStep = 'media-type' | 'title' | 'capture' | 'preview';

const TITLE_SUGGESTIONS = [
  'Tu aurais trouvé ?',
  "Cette question m'a détruit",
  "Personne de ma classe n'a réussi",
  'Facile ou difficile selon toi ?',
];

// ─── Utilitaires ─────────────────────────────────────────────────────────────

function detectShareType(
  phase: 'intro' | 'exercise',
  currentStep: ExerciseStep | null,
  stepHistory: StepHistoryEntry[],
): ShareType {
  if (phase === 'intro') return 'start';
  if (!currentStep && stepHistory.length > 0) return 'final';
  return 'step';
}

// ─── Utilitaires track ────────────────────────────────────────────────────────

function hasLiveAudioTrack(stream: MediaStream): boolean {
  const tracks = stream.getAudioTracks();
  return tracks.length > 0 && tracks[0]!.enabled && tracks[0]!.readyState === 'live';
}

function hasLiveVideoTrack(stream: MediaStream): boolean {
  const tracks = stream.getVideoTracks();
  return tracks.length > 0 && tracks[0]!.enabled && tracks[0]!.readyState === 'live';
}

async function capturePhoto(videoEl: HTMLVideoElement): Promise<Blob> {
  return toJpeg(videoEl, videoEl.videoWidth, videoEl.videoHeight);
}

/**
 * Photo de la galerie → JPEG entier (au plus 1920 px de côté) (une photo d'iPhone peut être
 * en HEIC, refusé par le bucket et illisible hors Safari).
 */
async function imageFileToJpeg(file: Blob): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return await toJpeg(img, img.naturalWidth, img.naturalHeight);
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function toJpeg(source: CanvasImageSource, vw: number, vh: number): Promise<Blob> {
  // l'image entière (comme à l'écran), au plus 1920 px de côté, jamais recadrée
  const { width, height } = photoFrame(vw, vh);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, vw, vh, 0, 0, width, height);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Échec capture photo'));
      },
      'image/jpeg',
      0.9,
    );
  });
}

function getBestVideoFormat(): { mimeType: string; contentType: string; ext: string } {
  // mp4 (H.264 + AAC) d'abord : lu par tous les navigateurs, iPhone compris
  // (un webm enregistré par Safari récent ne se relit pas partout) ; webm sinon.
  const candidates = [
    { mimeType: 'video/mp4;codecs=avc1,mp4a.40.2', contentType: 'video/mp4', ext: 'mp4' },
    { mimeType: 'video/mp4;codecs=avc1.42E01E,mp4a.40.2', contentType: 'video/mp4', ext: 'mp4' },
    { mimeType: 'video/mp4', contentType: 'video/mp4', ext: 'mp4' },
    { mimeType: 'video/webm;codecs=vp9,opus', contentType: 'video/webm', ext: 'webm' },
    { mimeType: 'video/webm;codecs=vp8,opus', contentType: 'video/webm', ext: 'webm' },
    { mimeType: 'video/webm', contentType: 'video/webm', ext: 'webm' },
  ];
  // Laisser mimeType vide : le navigateur choisit son format natif (Safari → mp4, Chrome → webm)
  return (
    candidates.find((c) => MediaRecorder.isTypeSupported(c.mimeType)) ?? {
      mimeType: '',
      contentType: '',
      ext: 'mp4',
    }
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

export function ChallengeShareModal({
  exerciseId,
  exerciseTitle,
  subject,
  level,
  chapter,
  phase,
  attemptId,
  currentStepIdx,
  currentStep,
  stepHistory,
  userId,
  onClose,
}: ChallengeShareModalProps) {
  const [modalStep, setModalStep] = useState<ModalStep>('media-type');
  const [mediaType, setMediaType] = useState<ChallengeMediaType>('none');
  const [title, setTitle] = useState('');
  // privé par défaut : la page d'accueil seulement si l'auteur le demande (validée par un admin)
  const [requestPublic, setRequestPublic] = useState(false);
  const [backgroundCardId, setBackgroundCardId] = useState<string | null>(null);
  const [mediaBlob, setMediaBlob] = useState<Blob | null>(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Caméra
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [hasAudio, setHasAudio] = useState<boolean | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoFormatRef = useRef<{ mimeType: string; contentType: string; ext: string } | null>(
    null,
  );

  const shareType = detectShareType(phase, currentStep, stepHistory);

  // Nettoyer le stream et l'URL preview quand le modal se ferme
  useEffect(() => {
    return () => {
      stream?.getTracks().forEach((t) => t.stop());
      if (mediaPreviewUrl) URL.revokeObjectURL(mediaPreviewUrl);
    };
  }, [stream, mediaPreviewUrl]);

  // ── Caméra ────────────────────────────────────────────────────────────────

  const startCamera = useCallback(async () => {
    try {
      let finalStream: MediaStream;

      if (mediaType === 'video') {
        try {
          // Requête unique audio+video : garantit que les deux pistes partagent le même contexte
          finalStream = await navigator.mediaDevices.getUserMedia({
            video: SELFIE_VIDEO_CONSTRAINTS,
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          });
        } catch {
          // Micro refusé ou indisponible — fallback vidéo seule
          finalStream = await navigator.mediaDevices.getUserMedia({
            video: SELFIE_VIDEO_CONSTRAINTS,
          });
        }
      } else {
        finalStream = await navigator.mediaDevices.getUserMedia({
          video: SELFIE_VIDEO_CONSTRAINTS,
        });
      }

      const audioTracks = finalStream.getAudioTracks();
      setHasAudio(
        audioTracks.length > 0 && audioTracks[0]!.enabled && audioTracks[0]!.readyState === 'live',
      );
      setStream(finalStream);
      if (videoRef.current) {
        videoRef.current.srcObject = finalStream;
        videoRef.current.play();
      }
    } catch {
      // Caméra refusée — fallback input file
      fileInputRef.current?.click();
    }
  }, [mediaType]);

  useEffect(() => {
    if (modalStep === 'capture' && mediaType !== 'none') {
      startCamera();
    }
    return () => {
      if (modalStep !== 'capture') {
        stream?.getTracks().forEach((t) => t.stop());
      }
    };
  }, [modalStep, startCamera]);

  const handleTakePhoto = useCallback(async () => {
    if (!videoRef.current) return;
    try {
      const blob = await capturePhoto(videoRef.current);
      const url = URL.createObjectURL(blob);
      setMediaBlob(blob);
      setMediaPreviewUrl(url);
      stream?.getTracks().forEach((t) => t.stop());
      setStream(null);
      setModalStep('preview');
    } catch {
      setError('Impossible de capturer la photo.');
    }
  }, [stream]);

  const handleStopRecording = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    recorderRef.current?.stop();
    setIsRecording(false);
  }, []);

  const handleStartRecording = useCallback(() => {
    if (!stream || !hasLiveVideoTrack(stream)) return;
    chunksRef.current = [];

    const fmt = getBestVideoFormat();
    videoFormatRef.current = fmt;

    if (process.env.NODE_ENV === 'development') {
      console.debug('[ChallengeShare] tracks', {
        video: stream
          .getVideoTracks()
          .map((t) => ({ label: t.label, enabled: t.enabled, readyState: t.readyState })),
        audio: stream
          .getAudioTracks()
          .map((t) => ({ label: t.label, enabled: t.enabled, readyState: t.readyState })),
      });
    }

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, selfieRecorderOptions(fmt.mimeType));
    } catch {
      // navigateur qui refuse les options de débit : format seul
      recorder = new MediaRecorder(stream, fmt.mimeType ? { mimeType: fmt.mimeType } : {});
    }
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      // Utiliser le mimeType réel du recorder plutôt que la supposition initiale
      const blobType =
        recorder.mimeType || chunksRef.current[0]?.type || fmt.contentType || 'video/mp4';
      const blob = new Blob(chunksRef.current, { type: blobType });
      const url = URL.createObjectURL(blob);
      setMediaBlob(blob);
      setMediaPreviewUrl(url);
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);

      // Mettre à jour le format avec les données réelles du recorder
      const actualMime = recorder.mimeType;
      if (actualMime) {
        const ext = actualMime.includes('mp4') ? 'mp4' : 'webm';
        const contentType = actualMime.split(';')[0] ?? fmt.contentType;
        videoFormatRef.current = { mimeType: actualMime, contentType, ext };
      }

      setModalStep('preview');
    };
    recorder.start(100);
    recorderRef.current = recorder;
    setIsRecording(true);
    setRecordingSeconds(0);
    timerRef.current = setInterval(() => {
      setRecordingSeconds((s) => {
        if (s >= 9) {
          handleStopRecording();
          return 10;
        }
        return s + 1;
      });
    }, 1000);

    // SpeechRecognition est intentionnellement désactivé pendant l'enregistrement vidéo.
    // Sur mobile, SpeechRecognition peut monopoliser le micro et empêcher MediaRecorder
    // de capturer l'audio. La transcription est traitée côté serveur après publication.
  }, [stream, handleStopRecording]);

  // ── Fichier fallback ──────────────────────────────────────────────────────

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      setError(null);
      // un fichier choisi remplace l'enregistrement : son type est le sien
      videoFormatRef.current = null;
      let media: Blob = file;
      if (mediaType === 'photo') {
        try {
          media = await imageFileToJpeg(file);
        } catch {
          setError('Impossible de lire cette photo : choisis-en une autre.');
          return;
        }
      } else {
        const issue = challengeMediaIssue(file, 'video');
        if (issue) {
          setError(issue);
          return;
        }
      }
      stream?.getTracks().forEach((t) => t.stop());
      setStream(null);
      setMediaBlob(media);
      setMediaPreviewUrl(URL.createObjectURL(media));
      setModalStep('preview');
    },
    [mediaType, stream],
  );

  // ── Publication ───────────────────────────────────────────────────────────

  // La publication (envoi de la vidéo compris) part en ARRIÈRE-PLAN : la fenêtre se ferme tout de suite,
  // un message dans l'app dit quand le défi est publié, et il apparaît dans le Feed.
  const handlePublish = useCallback(() => {
    setError(null);
    const { stepId, blockId } = resolveSharedStepRef({
      // Zéro V1 : `currentStep.id` est toujours un bloc statement_question.
      isV2: true,
      shareType,
      currentStepId: currentStep?.id ?? null,
    });
    const media = mediaType !== 'none' ? mediaBlob : null;
    // un média refusé (format, taille) se dit ici, avant de fermer
    if (media && mediaType !== 'none') {
      const issue = challengeMediaIssue(media as Blob & { name?: string }, mediaType);
      if (issue) {
        setError(issue);
        return;
      }
    }
    startBackgroundPublish({
      payload: {
        exerciseId,
        attemptId,
        stepId,
        blockId,
        shareType,
        title: title.trim(),
        mediaType,
        transcription: null, // traitée côté serveur après upload
        requestPublic,
      },
      mediaBlob: media,
      userId,
      videoMimeOverride: videoFormatRef.current ?? undefined,
      backgroundCardId: mediaType === 'none' ? backgroundCardId : null,
    });
    onClose();
  }, [
    exerciseId,
    attemptId,
    shareType,
    currentStep,
    title,
    mediaType,
    mediaBlob,
    userId,
    backgroundCardId,
    requestPublic,
    onClose,
  ]);

  // ── Navigation ────────────────────────────────────────────────────────────

  /** Refaire la photo / la vidéo : on jette la prise et on rouvre la caméra. */
  const retake = () => {
    stream?.getTracks().forEach((t) => t.stop());
    setStream(null);
    if (mediaPreviewUrl) URL.revokeObjectURL(mediaPreviewUrl);
    setMediaBlob(null);
    setMediaPreviewUrl(null);
    setError(null);
    setModalStep('capture');
  };

  const goBack = () => {
    if (modalStep === 'title') setModalStep('media-type');
    else if (modalStep === 'capture') {
      stream?.getTracks().forEach((t) => t.stop());
      setStream(null);
      setModalStep('title');
    } else if (modalStep === 'preview') {
      if (mediaType === 'none') setModalStep('title');
      else retake();
    }
  };

  // ── Rendu des étapes ──────────────────────────────────────────────────────

  return (
    <div className="ocean:bg-[#061433] fixed inset-0 z-50 flex flex-col bg-white">
      {/* En-tête */}
      <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-3">
        {modalStep !== 'media-type' ? (
          <button type="button" onClick={goBack} className="p-1 text-gray-500 hover:text-gray-900">
            <ChevronLeft className="h-6 w-6" />
          </button>
        ) : (
          <div className="w-7" />
        )}
        <span className="text-sm font-semibold text-gray-900">Lancer un défi</span>
        <button type="button" onClick={onClose} className="p-1 text-gray-500 hover:text-gray-900">
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Contexte exercice */}
      <div className="shrink-0 border-b border-gray-100 bg-gray-50 px-4 py-2.5">
        <p className="text-xs text-gray-500">
          <span className="font-medium text-gray-700">{exerciseTitle}</span>
          {' · '}
          {chapter} · {level}
          {shareType === 'step' && ` · Étape ${currentStepIdx + 1}`}
          {shareType === 'final' && ' · Résultat final'}
        </p>
      </div>

      {/* Corps */}
      <div className="flex flex-1 flex-col overflow-y-auto">
        {/* ── Étape 1 : type de média ── */}
        {modalStep === 'media-type' && (
          <MediaTypeStep
            onSelect={(type) => {
              setMediaType(type);
              setModalStep('title');
            }}
          />
        )}

        {/* ── Étape 2 : titre ── */}
        {modalStep === 'title' && (
          <TitleStep
            title={title}
            onChange={setTitle}
            onNext={() => {
              if (mediaType === 'none') setModalStep('preview');
              else setModalStep('capture');
            }}
          />
        )}

        {/* ── Étape 3 : capture ── */}
        {modalStep === 'capture' && (
          <CaptureStep
            mediaType={mediaType as 'photo' | 'video'}
            videoRef={videoRef}
            isRecording={isRecording}
            recordingSeconds={recordingSeconds}
            hasAudio={hasAudio}
            onTakePhoto={handleTakePhoto}
            onStartRecording={handleStartRecording}
            onStopRecording={handleStopRecording}
            fileInputRef={fileInputRef}
            onFileChange={(e) => void handleFileChange(e)}
            error={error}
          />
        )}

        {/* ── Étape 4 : preview + publication ── */}
        {modalStep === 'preview' && (
          <PreviewStep
            exerciseTitle={exerciseTitle}
            chapter={chapter}
            level={level}
            subject={subject}
            title={title}
            mediaType={mediaType}
            mediaPreviewUrl={mediaPreviewUrl}
            shareType={shareType}
            currentStepIdx={currentStepIdx}
            totalSteps={stepHistory.length + (currentStep ? 1 : 0)}
            error={error}
            onPublish={handlePublish}
            onRetake={mediaType === 'none' ? undefined : retake}
            requestPublic={requestPublic}
            onRequestPublic={setRequestPublic}
            background={
              mediaType === 'none' ? (
                <ChallengeBackgroundPicker
                  exerciseId={exerciseId}
                  value={backgroundCardId}
                  onChange={setBackgroundCardId}
                />
              ) : null
            }
          />
        )}
      </div>
    </div>
  );
}

// ─── Sous-composants ──────────────────────────────────────────────────────────

function MediaTypeStep({ onSelect }: { onSelect: (type: ChallengeMediaType) => void }) {
  return (
    <div className="flex flex-1 flex-col gap-3 p-6">
      <h2 className="mb-2 text-lg font-bold text-gray-900">Comment veux-tu te présenter ?</h2>
      {[
        {
          type: 'photo' as const,
          icon: Camera,
          label: 'Photo selfie',
          desc: 'Capture ton expression du moment',
        },
        {
          type: 'video' as const,
          icon: Video,
          label: 'Vidéo courte',
          desc: 'Max 10 secondes, pour te filmer',
        },
        {
          type: 'none' as const,
          icon: Minus,
          label: 'Sans média',
          desc: 'Juste le défi, rien de plus',
        },
      ].map(({ type, icon: Icon, label, desc }) => (
        <button
          key={type}
          type="button"
          onClick={() => onSelect(type)}
          className="hover:border-primary-400 hover:bg-primary-50 flex min-h-[4rem] items-center gap-4 rounded-2xl border border-gray-200 p-4 text-left transition-colors active:scale-[0.98]"
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gray-100">
            <Icon className="h-5 w-5 text-gray-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">{label}</p>
            <p className="text-sm text-gray-500">{desc}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

function TitleStep({
  title,
  onChange,
  onNext,
}: {
  title: string;
  onChange: (v: string) => void;
  onNext: () => void;
}) {
  const remaining = 120 - title.length;
  const isValid = title.trim().length >= 1;

  return (
    <div className="flex flex-1 flex-col p-6">
      <h2 className="mb-1 text-lg font-bold text-gray-900">Donne un titre à ton défi</h2>
      <p className="mb-4 text-sm text-gray-500">Max 120 caractères — accrocheur et fun !</p>

      <textarea
        autoFocus
        maxLength={120}
        value={title}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Ex : Tu aurais trouvé ?"
        rows={3}
        className="focus:border-primary-500 focus:ring-primary-500/20 w-full resize-none rounded-2xl border border-gray-200 p-4 text-base text-gray-900 outline-none placeholder:text-gray-400 focus:ring-2"
      />
      <p
        className={cn(
          'mt-1 text-right text-xs',
          remaining < 20 ? 'text-orange-500' : 'text-gray-400',
        )}
      >
        {remaining} restants
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {TITLE_SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onChange(s)}
            className="hover:border-primary-400 hover:text-primary-600 rounded-full border border-gray-200 px-3 py-1.5 text-sm text-gray-600 transition-colors active:scale-[0.98]"
          >
            {s}
          </button>
        ))}
      </div>

      <div className="mt-auto pt-6">
        <button
          type="button"
          disabled={!isValid}
          onClick={onNext}
          className="bg-primary-500 hover:bg-primary-600 w-full rounded-2xl py-4 text-base font-semibold text-white transition-colors active:scale-[0.98] disabled:opacity-40"
        >
          Continuer
        </button>
      </div>
    </div>
  );
}

function CaptureStep({
  mediaType,
  videoRef,
  isRecording,
  recordingSeconds,
  hasAudio,
  onTakePhoto,
  onStartRecording,
  onStopRecording,
  fileInputRef,
  onFileChange,
  error,
}: {
  mediaType: 'photo' | 'video';
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isRecording: boolean;
  recordingSeconds: number;
  hasAudio: boolean | null;
  onTakePhoto: () => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string | null;
}) {
  return (
    <div className="relative min-h-[60dvh] flex-1 bg-black" data-testid="challenge-capture">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        // l'image entière de la caméra, jamais recadrée : on se voit comme on sera filmé
        className="absolute inset-0 h-full w-full object-contain"
        style={{ transform: 'scaleX(-1)' }}
      />

      {/* Indicateur micro (mode vidéo uniquement) */}
      {mediaType === 'video' && !isRecording && hasAudio === false && (
        <div className="absolute left-3 right-3 top-4 rounded-xl bg-red-500/80 px-3 py-2 text-center text-xs font-medium text-white backdrop-blur-sm">
          Micro non disponible — la vidéo sera sans son
        </div>
      )}
      {mediaType === 'video' && !isRecording && hasAudio === true && (
        <div className="absolute left-3 right-3 top-4 rounded-xl bg-green-500/70 px-3 py-2 text-center text-xs font-medium text-white backdrop-blur-sm">
          🎙 Micro actif
        </div>
      )}

      {error && (
        <p
          role="alert"
          className="absolute left-3 right-3 top-16 rounded-xl bg-red-500/90 px-3 py-2 text-center text-sm font-medium text-white"
        >
          {error}
        </p>
      )}

      {/* Overlay commandes */}
      <div className="absolute bottom-8 left-0 right-0 flex flex-col items-center gap-4">
        {mediaType === 'photo' ? (
          <button
            type="button"
            onClick={onTakePhoto}
            className="h-16 w-16 rounded-full border-4 border-white bg-white/20 backdrop-blur-sm active:scale-95"
            aria-label="Prendre une photo"
          >
            <Camera className="mx-auto h-7 w-7 text-white" />
          </button>
        ) : (
          <>
            {isRecording && (
              <div className="rounded-full bg-black/60 px-4 py-1 text-sm font-bold text-red-400">
                ● {10 - recordingSeconds}s
              </div>
            )}
            <button
              type="button"
              onClick={isRecording ? onStopRecording : onStartRecording}
              className={cn(
                'h-16 w-16 rounded-full border-4 border-white backdrop-blur-sm transition-colors active:scale-95',
                isRecording ? 'bg-red-500' : 'bg-white/20',
              )}
              aria-label={isRecording ? "Arrêter l'enregistrement" : "Démarrer l'enregistrement"}
            >
              <Video className="mx-auto h-7 w-7 text-white" />
            </button>
          </>
        )}

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="text-xs text-white/70 underline"
        >
          Choisir depuis la galerie
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={mediaType === 'photo' ? 'image/*' : 'video/*'}
        capture="user"
        className="hidden"
        onChange={onFileChange}
      />
    </div>
  );
}

function PreviewStep({
  exerciseTitle,
  chapter,
  level,
  subject: _subject,
  title,
  mediaType,
  mediaPreviewUrl,
  shareType,
  currentStepIdx,
  totalSteps,
  error,
  onPublish,
  onRetake,
  background,
  requestPublic,
  onRequestPublic,
}: {
  exerciseTitle: string;
  chapter: string;
  level: string;
  subject: string;
  title: string;
  mediaType: ChallengeMediaType;
  mediaPreviewUrl: string | null;
  shareType: ShareType;
  currentStepIdx: number;
  totalSteps: number;
  error: string | null;
  onPublish: () => void;
  /** Refaire la photo / la vidéo (absent pour un défi sans média). */
  onRetake?: () => void;
  /** Choix du fond (défi sans photo ni vidéo). */
  background?: React.ReactNode;
  requestPublic: boolean;
  onRequestPublic: (v: boolean) => void;
}) {
  return (
    <div className="flex flex-1 flex-col p-6">
      <h2 className="mb-4 text-lg font-bold text-gray-900">Prévisualisation du défi</h2>
      {background && <div className="mb-4">{background}</div>}

      {/* Carte preview */}
      <div className="overflow-hidden rounded-3xl border border-gray-200 shadow-sm">
        {/* Media */}
        {mediaPreviewUrl && mediaType === 'photo' && (
          <img
            src={mediaPreviewUrl}
            alt="Selfie"
            className="max-h-[60dvh] w-full bg-black object-contain"
          />
        )}
        {mediaPreviewUrl && mediaType === 'video' && (
          <video
            src={mediaPreviewUrl}
            controls
            playsInline
            preload="metadata"
            className="max-h-[60dvh] w-full bg-black object-contain"
          />
        )}

        <div className="p-4">
          <p className="text-primary-500 text-xs font-medium uppercase tracking-wide">
            Défi Jeraisonne
          </p>
          <p className="mt-1 text-base font-bold text-gray-900">{title || '(sans titre)'}</p>

          <div className="mt-3 flex flex-wrap gap-2">
            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
              {level}
            </span>
            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
              {chapter}
            </span>
            {shareType === 'step' && totalSteps > 0 && (
              <span className="bg-primary-100 text-primary-700 rounded-full px-2.5 py-1 text-xs font-medium">
                Étape {currentStepIdx + 1}/{totalSteps}
              </span>
            )}
          </div>

          <p className="mt-3 line-clamp-2 text-sm text-gray-600">{exerciseTitle}</p>
        </div>
      </div>

      {onRetake && (
        <button
          type="button"
          onClick={onRetake}
          className="mt-3 flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 active:scale-[0.98]"
        >
          <RotateCcw className="h-4 w-4" aria-hidden />
          {mediaType === 'video' ? 'Refaire la vidéo' : 'Reprendre la photo'}
        </button>
      )}

      <ChallengeVisibilityChoice value={requestPublic} onChange={onRequestPublic} />

      {error && <p className="mt-3 rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</p>}

      <div className="mt-auto pt-6">
        <button
          type="button"
          onClick={onPublish}
          className="bg-primary-500 hover:bg-primary-600 w-full rounded-2xl py-4 text-base font-semibold text-white transition-colors active:scale-[0.98] disabled:opacity-60"
        >
          Publier le défi 🚀
        </button>
      </div>
    </div>
  );
}

/**
 * Visibilité du défi (migration 0726) : privé par défaut — sa page s'ouvre avec son lien,
 * il n'est ni sur la page d'accueil ni référencé. L'auteur peut demander la page d'accueil :
 * l'équipe valide avant.
 */
export function ChallengeVisibilityChoice({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="mt-4 rounded-2xl border border-gray-200 p-4">
      <p className="text-sm font-semibold text-gray-900">Ton défi est privé</p>
      <p className="mt-1 text-xs text-gray-600">
        Seules les personnes à qui tu envoies le lien peuvent le voir.
      </p>
      <label className="mt-3 flex min-h-[2.75rem] cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
          className="accent-primary-500 mt-0.5 h-5 w-5 shrink-0"
        />
        <span className="text-sm text-gray-800">
          Proposer mon défi sur la page d’accueil de JeRaisonne
          <span className="block text-xs text-gray-500">
            L’équipe le regarde avant de le publier.
          </span>
        </span>
      </label>
    </div>
  );
}
